"use client";

import dynamic from "next/dynamic";
import React, { useEffect, useState } from "react";
import ChartOne from "../Charts/ChartOne";
import ChartTwo from "../Charts/ChartTwo";
import ChatCard from "../Chat/ChatCard";
import TableOne from "../Tables/TableOne";
import CardDataStats from "../CardDataStats";
import * as geoappui from "ui";
import { PaginatedList } from "../Common/Pagination";
import LocationSelector from "../Location/Selector";
import { LocalSearchHeader } from "../LocalSearch/header";
import SearchResults from "../LocalSearch/results";
import ContentCard from "../Cards/Content";
import { GroupProfile } from "../Group/profile";
import { TabBar } from "../TabBar";
import { useSearchParams, useRouter } from 'next/navigation';
import { useLocation } from "../Location/Context";
import "mapbox-gl/dist/mapbox-gl.css"; // Import Mapbox styles
import BackButton from "../BackButton";
import { GetAnonymousViewMapDataQueryVariables, useGetAnonGeoActivityByActivityIdLazyQuery, useGetAnonymousViewMapDataLazyQuery, useSearchGeoActivitiesLazyQuery, useGetAnonGeoCodeByGeoCodeIdLazyQuery } from "@/generated/public/graphql";
import useSharedState from "@/hooks/useSharedState";
import { ApiClient } from "@/utils/apiClient";
import { useIsMobile } from "@/hooks/useIsMobile";
import { ListMapToggle } from "../Buttons/ToggleListMap";
import { FiEdit } from "react-icons/fi";
import axios from "axios";
import { buildQueryString } from "@/utils/links";
import { useConfig } from "@/context/ConfigContext";
import moment from 'moment-timezone';
const dateTimeToPriority = (dateTimeStr: string): number => {
  const date = new Date(dateTimeStr);
  // Return the Unix timestamp (in seconds)
  return Math.floor(date.getTime() / 1000);
};
const isInputDatePastNow = (inputString: string): boolean => {
  const localTz = moment.tz.guess(); // Get user's current timezone

  // Remove timezone from input and set it to the local timezone
  const inputWithoutTz = moment.parseZone(inputString).format("YYYY-MM-DDTHH:mm:ss");
  const inputWithLocalTz = `${inputWithoutTz}${moment().tz(localTz).format("Z")}`;

  // Parse both dates as local times for comparison
  const inputDate = moment(inputWithLocalTz);
  const currentDate = moment().local();

  // console.log({
  //   inputString,
  //   inputDate: inputDate.format(),
  //   currentDate: currentDate.format(),
  // })

  return inputDate.isBefore(currentDate);
};
interface LocationMarkerIcon {
  url: number;
  name: string;
}
interface LocationMarker {
  type: string;
  id: number;
  title: string;
  description: string;
  lng: number; // Add longitude
  lat: number; // Add latitude
  location?: string;
  eventDate?: string | null;
  eventTime?: string | null;
  eventTimestamp?: string | null;
  priority?: number;
  // metaJson?: any
  likes?: number;
  comments?: number;
  views?: number;
  media?: Array<{
    type: 'image' | 'video' | 'gallery';
    url: string[];
  }>;
  icon?: LocationMarkerIcon;
  refUrl?: string | null;
  searchable?: string[];
}
const MapOne = dynamic(() => import("@/components/Maps/MapOne"), {
  ssr: false
});
const ChartThree = dynamic(() => import("@/components/Charts/ChartThree"), {
  ssr: false
});
const LocalSearch: React.FC = () => {
  const router = useRouter();
  const isMobile = useIsMobile();
  const config = useConfig();
  // console.log("config:::", config)
  // console.log({
  //   isMobile
  // })
  const mapRef = React.useRef<mapboxgl.Map | null>(null);
  const {
    location: searchLocation,
    setLocation: setSearchLocation
  } = useLocation();
  const searchParams = useSearchParams();
  // Store params in state to prevent stale values
  const [params, setParams] = useState(new URLSearchParams(searchParams.toString()));
  useEffect(() => {
    // Sync local state with URL changes
    setParams(new URLSearchParams(searchParams.toString()));
  }, [searchParams]);
  const setSearchParams = (newParams: URLSearchParams) => {
    // Update the URL with new search params
    setParams(newParams);
    router.replace(`?${newParams.toString()}`, {
      scroll: false
    });
  };
  const clearParam = (param: string | string[]) => {
    // Clear one or multiple query params
    const newParams = new URLSearchParams(params.toString());
    if (Array.isArray(param)) {
      param.forEach(p => newParams.delete(p));
    } else {
      newParams.delete(param);
    }
    setSearchParams(newParams);
  };
  const setParam = (param: string, value: string) => {
    // Set a single query param
    const newParams = new URLSearchParams(params.toString());
    newParams.set(param, value);
    setSearchParams(newParams);
  };
  const dropIdParam = searchParams.get('dropId');
  const castIdParam = searchParams.get('castId');
  const activityIdParam = searchParams.get('activityId');
  const [activityId, setActivityId] = useState<any>(dropIdParam || castIdParam || activityIdParam);
  const placeIdParam = searchParams.get('placeId');
  const [place, setPlace] = useState<any>();
  const [placeId, setPlaceId] = useState<any>(placeIdParam);
  // TODO: Use JIT decrption of the placeId value - let's encode the google:sdfdsf so that people don't know w'ere using google place ids directly.
  const searchParam = searchParams.get('search');
  const locationIdParam = searchParams.get('location');
  const [locationIdParamLast, setLocationIdParamLast] = useState<string>();
  const [showProfileSection, setShowProfileSection] = useState<boolean>(false);
  const [activity, setActivity] = useState<any>();
  const [displayedResults, setDisplayedResults] = useState<any>();
  const lastSerializedDataRef = React.useRef<string | null>(null);
  const [selectedIcon, setSelectedIcon] = useSharedState('selectedIcon', null);
  const [isMapMode, setIsMapMode] = useState(false);
  const [getAnonymousViewMapData, {
    loading,
    error,
    data,
    refetch
  }] = useGetAnonymousViewMapDataLazyQuery({
    fetchPolicy: 'network-only' // Forces a fresh request each time
  });
  const [getSearchGeoActivitiesLazyQuery, {
    loading: loadingSearch,
    error: errorSearch,
    data: dataSearch,
    refetch: refetchSearch
  }] = useSearchGeoActivitiesLazyQuery({
    fetchPolicy: 'network-only' // Forces a fresh request each time
  });
  const [loadAnonGeoCode, {
    error: errorPlace,
    loading: loadingPlace,
    data: dataPlace
  }] = useGetAnonGeoCodeByGeoCodeIdLazyQuery({
    // client: isAnonymousUser ? clientPublic : undefined,
    fetchPolicy: 'network-only' // Forces a fresh request each time
  });
  function getMapBoundaries() {
    if (mapRef.current) {
      const bounds = mapRef.current.getBounds();
      if (bounds) {
        const southWest = bounds.getSouthWest();
        const northEast = bounds.getNorthEast();
        // Map the result to match the required structure
        const result = {
          left: southWest.lat,
          // Latitude of southwest corner (matches left)
          bottom: southWest.lng,
          // Longitude of southwest corner (matches bottom)
          right: northEast.lat,
          // Latitude of northeast corner (matches right)
          top: northEast.lng // Longitude of northeast corner (matches top)
        };
        // console.log("getMapBoundaries:", result)
        return result;
      }
    }
  }
  function formatDateRange(startDate: string, endDate?: string): {
    eventDate: string;
    eventTime: string;
  } {
    const dateOptions: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      // 'Thu'
      year: 'numeric',
      // '2024'
      month: 'short',
      // 'Oct'
      day: 'numeric' // '3'
    };
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      // '7 PM'
      minute: 'numeric' // '00'
    };
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : null;
    const eventDate = start.toLocaleDateString(undefined, dateOptions);
    const startTime = start.toLocaleTimeString(undefined, timeOptions);
    let eventTime: string;
    if (!endDate || start.toDateString() === end?.toDateString()) {
      // Same day or no end date
      const endTime = end ? end.toLocaleTimeString(undefined, timeOptions) : null;
      eventTime = endTime && startTime !== endTime ? `${startTime} - ${endTime}` : `${startTime}`;
    } else {
      // Different day
      const endTime = end!.toLocaleTimeString(undefined, timeOptions);
      const endDateDisplay = end!.toLocaleDateString(undefined, dateOptions);
      eventTime = `${startTime} - ${endTime} (${endDateDisplay})`;
    }
    return {
      eventDate,
      eventTime
    };
  }
  const mapDropToLocationMarker = (drop: any) => {
    const {
      eventDate,
      eventTime
    } = formatDateRange(drop?.displayStartDate, drop?.displayEndDate);
    const mediaData: {
      type: 'image' | 'video' | 'gallery';
      url: string[];
    }[] = [];
    if (drop?.refImageUrl) {
      mediaData.push({
        type: 'image',
        url: [drop?.refImageUrl]
      });
    }
    if (drop?.refVideoUrl) {
      mediaData.push({
        type: 'video',
        url: [drop?.refVideoUrl]
      });
    }
    const proxyIconUrl = `/api/proxy-image?url=${encodeURIComponent(drop?.appDropCategory?.iconUrl)}`;
    return {
      id: drop?.id,
      type: 'drop',
      isEvent: drop?.appDropCategory?.isEvent,
      title: drop?.messageSubject,
      description: drop?.messageBody,
      lat: drop?.geocode?.locationCoordinates?.latitude,
      lng: drop?.geocode?.locationCoordinates?.longitude,
      location: drop?.geocode?.name,
      eventDate: eventDate,
      eventTime: eventTime,
      eventTimestamp: drop?.displayStartDate,
      priority: dateTimeToPriority(drop?.displayStartDate),
      refUrl: drop?.refUrl,
      searchable: [drop?.messageSubject, drop?.messageBody, `icon:${drop?.appDropCategory?.name}`],
      media: mediaData,
      // metaJson: drop?.metaJson,
      views: drop?.metaJson?.track?.views || 0,
      likes: drop?.likeCount || 0,
      comments: drop?.commentCount || 0,
      icon: {
        url: proxyIconUrl as any,
        name: drop?.appDropCategory?.name
      }
    };
  };
  const mapPlaceToLocationMarker = (place: any) => {
    // console.log({
    //   mapPlaceToLocationMarker: place
    // })
    // const { eventDate, eventTime } = formatDateRange(drop?.displayStartDate, drop?.displayEndDate)
    const mediaData: {
      type: 'image' | 'video' | 'gallery';
      url: string[];
    }[] = [];
    if (place?.placeInfoImageUrl) {
      mediaData.push({
        type: 'image',
        url: [place?.placeInfoImageUrl]
      });
      // loop: placeInfoImageGallery
    }
    let placeCategoryIconPrimary = place?.googlePlaceIdData?.categories?.length > 0 ? place?.googlePlaceIdData?.categories?.filter((category: any) => category?.name in config?.iconMap)[0] : undefined; // category?.primary === true || 
    // console.log({
    //   placeCategoryIconPrimary
    // })
    const placeIconUrl = place?.placeInfoIconUrl || (placeCategoryIconPrimary?.name && config?.iconMap[placeCategoryIconPrimary?.name] ? config?.iconMap[placeCategoryIconPrimary?.name]?.url : undefined) || 'https://vmajtfunkcztveldukoz.supabase.co/storage/v1/object/public/ugeo-app-images/managed/places/general.png';
    const proxyIconUrl = `/api/proxy-image?url=${encodeURIComponent(placeIconUrl)}`;
    const placeType = placeCategoryIconPrimary?.name || 'Place';
    // console.log({
    //   placeIconUrl
    // })
    return {
      id: place?.id,
      type: 'place',
      title: place?.name,
      description: place?.placeInfoDescription,
      lat: place?.latitude || place?.locationCoordinates?.latitude,
      lng: place?.longitude || place?.locationCoordinates?.longitude,
      location: place?.locationInfoAddressLine1,
      eventDate: null,
      eventTime: null,
      eventTimestamp: null,
      priority: 1,
      placeIdUrl: place?.googlePlaceIdUrl,
      refUrl: null,
      searchable: [place?.name, place?.placeInfoDescription, `icon:${placeType}`],
      media: mediaData,
      // metaJson: place?.metaJson,
      views: place?.metaJson?.track?.views || 0,
      likes: place?.socialLikeCount || 0,
      comments: place?.socialCommentCount || 0,
      icon: {
        url: proxyIconUrl as any,
        name: placeType
      }
    };
  };
  React.useEffect(() => {
    if (!searchParam) {
      // clearParam("search")
    }
  }, [searchParams]);
  useEffect(() => {
    if (activityId || placeId) return;
    // console.log("data refresh called", {
    //   searchParam,
    //   data, loading, error,
    //   dataSearch, loadingSearch, errorSearch
    // })
    // Use a ref to store the last serialized version of the data
    if (loading || error || loadingSearch || errorSearch) return;
    const selectData = data?.getAnonMapFeed?.geoDrops;
    const selectPlaceData = data?.getAnonMapFeed?.places;
    const mappedData: LocationMarker[] = [];
    // console.log(JSON.stringify(selectData, null, 2))
    // console.log(data)

    // Map over the geoDrops and transform them into the desired format
    for (const drop of selectData || []) {
      if (drop?.type?.toLowerCase() === 'drop' && drop?.appDropCategory?.isEvent === true && isInputDatePastNow(drop?.displayStartDate)) {
        continue;
      }
      // console.log("metaJson:", drop?.metaJson, )
      mappedData.push(mapDropToLocationMarker(drop));
    }
    for (const place of selectPlaceData || []) {
      mappedData.push(mapPlaceToLocationMarker(place));
    }
    // Serialize the new mappedData to compare with the last version
    const newSerializedData = JSON.stringify(mappedData);

    // Compare with the last cached serialized data
    if (newSerializedData !== lastSerializedDataRef.current) {
      // Update displayed results if the data has changed
      setDisplayedResults(mappedData);

      // Update the ref with the new serialized data
      lastSerializedDataRef.current = newSerializedData;
    }
  }, [data, loading, error, dataSearch, loadingSearch, errorSearch]);
  const getMapData = (searchParamIn?: string | null) => {
    getAnonymousViewMapData({
      variables: {
        sharingAndFilterCriteria: JSON.stringify({
          searchText: searchParamIn,
          showPlaces: true
          // showPlacesFromGoogle: true, // ONLY FOR TESTING PURPOSES
        }),
        boundary: {
          ...getMapBoundaries()
        } as GetAnonymousViewMapDataQueryVariables['boundary']
      }
    });
  };

  // const debouncedGetMapData = React.useCallback(debounce(getMapData, 500), [searchParam, popularParam]);

  React.useEffect(() => {
    getMapData(searchParam);
  }, [searchParams]);
  React.useEffect(() => {
    if (locationIdParam && locationIdParam !== locationIdParamLast) {
      const match = locationIdParam.match(/latlng\((-?\d+\.\d+),(-?\d+\.\d+)\)/);
      if (match) {
        const mapOpenLat = parseFloat(match[1]);
        const mapOpenLng = parseFloat(match[2]);
        const HERE_API_KEY = config?.app?.settings?.hereApiKey;
        const qParams = buildQueryString({
          apikey: HERE_API_KEY,
          at: `${mapOpenLat},${mapOpenLng}`
        });
        const HERE_API_GEOCODER_URL = "https://revgeocode.search.hereapi.com/v1/revgeocode";
        const url = `${HERE_API_GEOCODER_URL}?${qParams}`;
        axios.get(url).then(result => {
          if (result?.data?.items?.length) {
            const addressLabel = result?.data?.items[0]?.address?.label;
            if (addressLabel) {
              setSearchLocation({
                location: addressLabel,
                updateMap: !locationIdParamLast ? true : false // only on page init/load 
              });
            }
          }
        });
      } else {
        setSearchLocation({
          location: locationIdParam,
          updateMap: !locationIdParamLast ? true : false // only on page init/load 
        });
      }
    }
  }, [locationIdParamLast]);
  React.useEffect(() => {
    if (locationIdParam && locationIdParam !== locationIdParamLast) {
      setLocationIdParamLast(locationIdParam);
    }
  }, [locationIdParam]);
  React.useEffect(() => {
    if (searchLocation?.location) {
      setParam("location", searchLocation?.location);
    }
  }, [searchLocation]);
  const viewActivity = async (idIn: string) => {
    // console.log("viewActivity called")
    const apiClient = new ApiClient();
    const result = await apiClient.post(`/track/view-activity?id=${idIn}`);
    // console.log("viewActivity-response:", result)
  };
  const viewPlace = async (idIn: string) => {
    if (`${idIn}`.startsWith('google:')) return;
    // console.log("viewPlace called")
    const apiClient = new ApiClient();
    const result = await apiClient.post(`/track/view-location?id=${idIn}`);
    // console.log("viewPlace-response:", result)
  };
  const clearSelection = () => {
    clearParam(["activityId", "castId", "dropId", "placeId"]);
    setActivityId(null);
    setActivity(null);
    setPlaceId(null);
    setPlace(null);
  };
  const [getAnonGeoActivityByActivityId, {
    loading: loadingActivity,
    error: errorActivity,
    data: dataActivity
  }] = useGetAnonGeoActivityByActivityIdLazyQuery();
  const scrollToTop = () => {
    const overlay = document.querySelector('.overlayRef'); // Use your overlay class name here
    if (overlay) {
      overlay.scrollTop = 0; // Scroll to the top
    }
  };
  React.useEffect(() => {
    if (!activityId || activityId && !activity) {
      if (activityId && !activity) {
        const apiActivityData = dataActivity?.geoActivities?.nodes[0];
        if (!apiActivityData) {
          // console.log("call api to refresh by id")
          getAnonGeoActivityByActivityId({
            variables: {
              id: activityId
            }
          });
        } else if (!activity?.id) {
          // console.log("mapDropToLocationMarker(apiActivityData):::", mapDropToLocationMarker(apiActivityData))
          setActivity(mapDropToLocationMarker(apiActivityData));
        }
      }
      // else {
      //   clearSelection()
      // }
    } else {
      setParam("activityId", activityId);
      if (activity?.id) {
        viewActivity(activity?.id);
        scrollToTop();
      }
    }
  }, [activityId, activity, dataActivity]);
  const getPlaceDetails = async (googlePlaceId: string) => {
    let results = {};
    const url = `/public/place-details-v2?${buildQueryString({
      googlePlaceId: googlePlaceId,
      fallbackLat: 'fallbackLat',
      fallbackLng: 'fallbackLng',
      fallbackAddress: 'fallbackAddress',
      fallbackName: 'fallbackName'
    })}`;
    const apiClient = new ApiClient();
    const placeDetailResponse: any = await apiClient.get(url);
    // console.log("placeDetailResponse::", placeDetailResponse)
    if (placeDetailResponse.status === 'success') {
      setPlaceId(placeDetailResponse?.message?.id);
    }
  };
  React.useEffect(() => {
    // console.log({
    //   placeId, 
    //   place, 
    //   errorPlace,
    //   loadingPlace,
    //   dataPlace
    // })
    if (!placeId || placeId && !place) {
      if (placeId && !place) {
        // console.log("got placeId but not data")
        const apiPlaceData = dataPlace?.geoCodes?.nodes[0];
        if (!apiPlaceData) {
          if (`${placeId}`.startsWith('google:')) {
            // console.log("call api to refresh by google place id")
            const googlePlaceId = `${placeId}`.replace("google:", "");
            getPlaceDetails(googlePlaceId);
          } else {
            // console.log("call api to refresh by geocode id")
            loadAnonGeoCode({
              variables: {
                geoCodeId: placeId
              }
            });
          }
        } else if (!place?.id) {
          // console.log(" no place?.id -----")
          setPlace(mapPlaceToLocationMarker(apiPlaceData));
        }
      }
      // else {
      //   console.log("clearing selection")
      //   clearSelection()
      // }
    } else {
      setParam("placeId", placeId);
      if (place?.id) {
        if (place?.location && searchLocation?.location) {
          setSearchLocation({
            location: place?.location,
            updateMap: false
          });
        }
        viewPlace(place?.id);
        scrollToTop();
      }
    }
  }, [placeId, place, dataPlace]);
  const calculateDynamicOffsetY = (offsetYTarget = 300) => {
    if (mapRef.current) {
      const mapHeight = mapRef.current.getContainer().clientHeight;
      // Calculate the dynamic offset by subtracting half the map's height from the target offset
      const dynamicOffsetY = mapHeight / 2 - offsetYTarget;
      return dynamicOffsetY;
    }
    return 0; // Default offset in case mapRef is not available
  };
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const toggleEditMode = () => {
    setIsEditing(!isEditing);
  };
  const isLoading = loading || loadingActivity || loadingSearch;
  return <geoappui.MapboxMap ref={mapRef} isMobile={isMobile} location={searchLocation} setLocation={setSearchLocation} setActivity={setActivity} setActivityId={setActivityId} setPlace={setPlace} setPlaceId={setPlaceId} refreshData={() => {
    getMapData(searchParam);
  }} focus={activity?.id ? {
    type: 'activity',
    content: activity
  } : place?.id ? {
    type: 'place',
    content: place
  } : undefined} displayedResults={searchParam == null || searchParam != null && !!searchParam ? displayedResults || [] : []} displayedResultsFilter={selectedIcon ? `icon:${selectedIcon}` : null} addCenterOffset={!isMobile && (displayedResults || activity?.id || place?.id) ? {
    offsetX: -150,
    offsetY: calculateDynamicOffsetY()
  } : null} initCenter={[-73.935242, 40.730610] // NYC, NY
  } data-sentry-element="unknown" data-sentry-component="LocalSearch" data-sentry-source-file="LocalSearch.tsx">
      {<>
          <div className="flex">
            <div className="">

              {isLoading ? <div className="pl-4 pb-2"><p>loading...</p></div> : <></>}

              <div className={"px-4"}>

                {activityId || placeId ? <BackButton onBack={() => {
              clearSelection();
            }} header={displayedResults ? "Results" : "Go Back"} /> : <></>}
              </div>

              {isMobile && !activityId && !showProfileSection ? <div className="flex items-start">
                  <>
                    <ListMapToggle onToggle={(isMapMode: boolean) => {
                setIsMapMode(isMapMode);
              }} />
                  </>
                  {isEditing ? <>
                      <LocationSelector apiKey={process.env.NEXT_PUBLIC_GOOGLE_API_KEY || ''} hideHeader={true} initEditMode={true} isEditingCallback={setIsEditing} />
                    </> : <>
                      <p className="font-bold pl-2 pt-2">{searchLocation?.location}</p> 
                    </>}
                  {<FiEdit size={isEditing ? 30 : 20} className={"text-gray-500 cursor-pointer ml-4 mt-2" + (isEditing ? "mb-2" : "")} onClick={toggleEditMode} />}
                </div> : <></>}
              {!isMobile ? <LocationSelector apiKey={process.env.NEXT_PUBLIC_GOOGLE_API_KEY || ''} /> : <></>}

              <>
                {showProfileSection ? <GroupProfile /> : <></>}
              </>

              {activityId || placeId ? <ContentCard content={{
            ...(activityId ? activity : undefined),
            ...(placeId ? place : undefined)
          }} onLocationClick={(resultId, result) => {
            // console.log({
            //   resultId,
            //   result
            // })
            if (result?.type === 'drop') {
              setActivityId(resultId);
              setActivity(result);
            } else if (result?.type === 'place') {
              setPlaceId(resultId);
              setPlace(result);
            }
          }} /> : displayedResults?.length > 0 && (!isMobile || !isMapMode) && searchLocation?.location && (searchParam == null || searchParam != null && !!searchParam) ? <SearchResults displayedResults={displayedResults} header={searchParam == null ? "Popular" : "Search Results"} onResultView={activityId ? viewActivity : placeId ? viewPlace : undefined} onResultClick={(resultId, result) => {
            // console.log({
            //   resultId,
            //   result
            // })
            if (result?.type === 'drop') {
              setActivityId(resultId);
              setActivity(result);
            } else if (result?.type === 'place') {
              setPlaceId(resultId);
              setPlace(result);
            }
            // if (mapRef.current) {
            //   mapRef.current.zoomTo(16)
            // }
          }} /> : <></>}
            </div>
          </div>
        </>}
    </geoappui.MapboxMap>;
};
export default LocalSearch;